import { useMemo } from "react";
import {
  ApiPermissions,
  DropListWithIconItem,
  EXCHANGE_STATUS,
  ExchangeInfo,
  ExchangeInfoById,
  FeaturesKeys,
  featureKeyToPermission,
  userFacingMessages,
} from "@cede/types";
import { API_ENDPOINT, AVAILABLE_EXCHANGES_CACHE_TIME, QueryKeys, SUPPORTED_EXCHANGES_CACHE_TIME } from "@cede/utils";
import { useApi } from "./useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

type ExchangeAvailability = Record<string, boolean>;

export const useSupportedExchanges = () => {
  const { hubUser, authenticated } = useHubUser();
  const apiHelper = useApi();
  const exchangeAccountsKey = useMemo(() => {
    if (!hubUser?.exchangeAccounts) return "none";
    return hubUser.exchangeAccounts
      .map((account) => account.id)
      .sort((a, b) => a - b)
      .join("|");
  }, [hubUser?.exchangeAccounts]);

  const {
    data: supportedExchanges,
    error,
    isLoading,
    refetch,
  } = useQuery<ExchangeInfo[] | undefined, Error>({
    queryKey: [QueryKeys.SUPPORTED_EXCHANGES, exchangeAccountsKey],
    queryFn: async () => {
      return await apiHelper.getOrThrow(API_ENDPOINT.HUB_SUPPORTED_EXCHANGES);
    },
    staleTime: SUPPORTED_EXCHANGES_CACHE_TIME,
    gcTime: SUPPORTED_EXCHANGES_CACHE_TIME,
  });

  const {
    data: availableExchanges,
    isFetching: isUnavailableExchangesLoading,
    refetch: refetchUnavailableExchanges,
  } = useQuery<ExchangeAvailability | undefined>({
    queryKey: [QueryKeys.AVAILABLE_EXCHANGES, exchangeAccountsKey],
    queryFn: async () => {
      if (!supportedExchanges || !authenticated) return;
      const availability: ExchangeAvailability = {};
      for (const exchange of supportedExchanges) {
        availability[exchange.id] = true;
      }
      return availability;
    },
    enabled: authenticated && !!supportedExchanges,
    staleTime: AVAILABLE_EXCHANGES_CACHE_TIME,
    gcTime: AVAILABLE_EXCHANGES_CACHE_TIME,
  });

  const supportedExchangesById = useMemo(() => {
    if (!supportedExchanges) return {};

    return supportedExchanges.reduce((acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    }, {} as ExchangeInfoById);
  }, [supportedExchanges]);

  const downExchanges = useMemo(() => {
    if (!supportedExchanges) return [];

    const badStatuses = new Set<EXCHANGE_STATUS>([
      EXCHANGE_STATUS.SHUTDOWN,
      EXCHANGE_STATUS.ERROR,
      EXCHANGE_STATUS.MAINTENANCE,
    ]);

    return supportedExchanges.filter((exchange) => badStatuses.has(exchange.status));
  }, [supportedExchanges]);

  const downExchangesIds = useMemo(() => downExchanges.map((exchange) => exchange.id), [downExchanges]);

  const unavailableExchangesIds = useMemo(() => {
    if (!availableExchanges) return [];

    return Object.entries(availableExchanges)
      .filter(([, isAvailable]) => !isAvailable)
      .map(([exchangeId]) => exchangeId);
  }, [availableExchanges]);

  const isRequiringWhitelist = (permissions: { type: ApiPermissions }[], exchangeId: string) => {
    const exchange = supportedExchangesById[exchangeId];
    if (!exchange) return false;
    return permissions.some((permission) => exchange.whitelistScopes?.includes(permission.type));
  };

  const shouldDisableCex =
    (feature: FeaturesKeys) =>
    (option: DropListWithIconItem<{ cexValue?: string; permissions?: ApiPermissions[] }>) => {
      if (!option.cexValue) return false;
      if (supportedExchangesById[option.cexValue]?.featuresUnderMaintenance?.[feature]) return true;
      if (!option.permissions?.includes(featureKeyToPermission[feature])) return true;

      return false;
    };

  const disabledCexText =
    (
      feature: FeaturesKeys,
      MissingPermissionElement: React.FC<{
        option: DropListWithIconItem<{ cexValue?: string; permissions?: ApiPermissions[] }>;
      }>,
    ) =>
    (option: DropListWithIconItem<{ cexValue?: string; permissions?: ApiPermissions[] }>) => {
      if (!option.cexValue) return userFacingMessages.EXCHANGES_STATUSES.TEMPORARILY_UNAVAILABLE;
      if (supportedExchangesById[option.cexValue]?.featuresUnderMaintenance?.[feature])
        return userFacingMessages.EXCHANGES_STATUSES.UNDER_MAINTENANCE;
      if (!option.permissions?.includes(featureKeyToPermission[feature]))
        return <MissingPermissionElement option={option} />;

      return userFacingMessages.EXCHANGES_STATUSES.TEMPORARILY_UNAVAILABLE;
    };

  const isFeatureSupportedByCex = (exchangeId: string, feature: FeaturesKeys) => {
    return supportedExchangesById[exchangeId]?.supportedFeatures?.includes(feature);
  };

  const hasAtLeastOneCexSupported = (feature: FeaturesKeys) => {
    return !!supportedExchanges?.find((exchange) => exchange.supportedFeatures.includes(feature));
  };

  return {
    refetchSupportedExchanges: refetch,
    supportedExchanges,
    downExchanges,
    downExchangesIds,
    supportedExchangesById,
    isSupportedExchangesLoading: isLoading,
    supportedExchangesError: error,
    isRequiringWhitelist,
    shouldDisableCex,
    disabledCexText,
    isFeatureSupportedByCex,
    hasAtLeastOneCexSupported,
    unavailableExchangesIds,
    isUnavailableExchangesLoading,
    refetchUnavailableExchanges,
  };
};
